import React, { forwardRef, useEffect } from 'react';
import type { Selection } from '@heroui/react';
import { Select, SelectItem, SelectProps } from '@heroui/react';
import cn from 'classnames';
import styles from './CustomSelect.module.scss';

interface CustomSelectProps extends Omit<SelectProps, 'style' | 'children'> {
  style: 'black' | 'color' | 'purple';
  items: { key: string; label: string }[];
  defaultValue?: string[];
  selectedKeys?: Selection;
  errorMessage?: string;
  showError?: boolean;
  getSelectedMultiValues?: (value: Selection) => void;
  disableTextCurrent?: boolean;
}

export const CustomSelect = forwardRef<HTMLDivElement, CustomSelectProps>((props, ref) => {
  const {
    items,
    style,
    defaultValue = [],
    getSelectedMultiValues,
    errorMessage,
    showError = false,
    selectedKeys,
    disableTextCurrent = false,
    ...selectProps
  } = props;

  const [value, setValue] = React.useState<Selection>(selectedKeys || new Set(defaultValue));

  const classnamesBlackSelect = {
    innerWrapper: styles.blackInnerWrapper,
    popoverContent: styles.blackPopoverContent,
    listbox: styles.blackListbox,
  };

  const classnamesColorSelect = {
    base: 'max-w-[185px]',
    innerWrapper: styles.colorInnerWrapper,
    popoverContent: styles.colorPopoverContent,
    listbox: styles.colorListbox,
  };

  const classnamesPurpleSelect = {
    base: 'max-w-[185px]',
    innerWrapper: styles.purpleInnerWrapper,
    popoverContent: styles.purplePopoverContent,
    listbox: styles.purpleListbox,
  };

  const getColorTriggerStyle = (
    selectedValue: Selection,
    items: { key: string; label: string }[],
  ) => {
    const selectedKey = Array.from(selectedValue)[0];
    if (selectedKey === undefined) {
      return '';
    }

    const selectedItem = items.find((item) => item.key === selectedKey.toString());
    const label = selectedItem?.label || '';

    if (['Схвалений', 'Відправлено', 'Сплачено', 'Виконано', 'Активний'].includes(label)) {
      return styles.colorTriggerGreen;
    }
    if (
      [
        'Очікує на модерацію',
        'Заплановано',
        'Очікує оплати',
        'Очікує перевірки',
        'Прихований',
      ].includes(label)
    ) {
      return styles.colorTriggerYellow;
    }
    if (['Відхилений', 'Скасовано', 'Доопрацювання', 'Закритий'].includes(label)) {
      return styles.colorTriggerRed;
    } else {
      return styles.colorTrigger;
    }
  };

  const [isUserInteraction, setIsUserInteraction] = React.useState(false);
  const [previousValue, setPreviousValue] = React.useState<Selection>(new Set());

  useEffect(() => {
    if (getSelectedMultiValues && isUserInteraction) {
      const currentValueStr = Array.from(value).sort().join(',');
      const previousValueStr = Array.from(previousValue).sort().join(',');

      if (currentValueStr !== previousValueStr) {
        getSelectedMultiValues(value);
        setPreviousValue(value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSelectedMultiValues, value, isUserInteraction, previousValue]);

  const handleSelectionChange = (newValue: Selection) => {
    setValue(newValue);
    setIsUserInteraction(true);
    if (selectProps.onSelectionChange) {
      selectProps.onSelectionChange(newValue);
    }
  };

  useEffect(() => {
    if (selectedKeys) {
      setValue(selectedKeys);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKeys]);

  const isInvalid = !!errorMessage;
  return (
    <div className="flex flex-col gap-y-1" ref={ref}>
      <Select
        aria-label="Select"
        labelPlacement="outside"
        selectedKeys={value}
        onSelectionChange={handleSelectionChange}
        items={items}
        isInvalid={isInvalid}
        errorMessage={showError ? errorMessage : ''}
        classNames={{
          ...(style === 'black'
            ? classnamesBlackSelect
            : style === 'purple'
              ? classnamesPurpleSelect
              : classnamesColorSelect),
          label: cn(styles.label, isInvalid && 'error'),
          errorMessage: 'text-danger',
          value: cn(
            Array.from(value).length > 0
              ? disableTextCurrent
                ? ''
                : '!text-current'
              : '!text-grey-secondary',
          ),
          trigger: cn(
            style === 'black'
              ? styles.blackTrigger
              : style === 'purple'
                ? styles.purpleTrigger
                : cn(styles.colorTrigger, getColorTriggerStyle(value, items)),
            isInvalid ? '!border-danger' : '',
          ),
        }}
        {...selectProps}
      >
        {(item) => <SelectItem>{item.label}</SelectItem>}
      </Select>
    </div>
  );
});

CustomSelect.displayName = 'CustomSelect';
